import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/industries", label: "Industries" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-black/70 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
          <div className="w-7 h-7 bg-[#0055FF] flex items-center justify-center">
            <span className="font-display text-white text-sm leading-none">P</span>
          </div>
          <span className="font-display text-white text-lg tracking-tight">
            PRAGMA<span className="text-[#0055FF]">.</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-link-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-medium tracking-tight transition-colors ${
                  isActive ? "text-white" : "text-white/60 hover:text-white"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link to="/contact" className="hidden sm:block" data-testid="nav-cta-start">
            <Button className="bg-[#0055FF] hover:bg-[#0044cc] text-white rounded-none h-10 px-5 font-medium tracking-tight">
              Start a project <ArrowUpRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
          <button
            className="lg:hidden text-white p-2"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-toggle-mobile"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-black/95 border-t border-white/10" data-testid="nav-mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                className={({ isActive }) =>
                  `text-base font-medium ${isActive ? "text-white" : "text-white/70"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

export const Footer = () => (
  <footer className="bg-[#0a0a0a] text-white border-t border-white/10 relative overflow-hidden" data-testid="site-footer">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-20 pb-10">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <h3 className="font-display text-3xl md:text-4xl tracking-tighter leading-[1.05]">
            Ready to turn data into <br />
            <span className="text-[#0055FF]">measurable revenue?</span>
          </h3>
          <Link to="/contact" data-testid="footer-cta">
            <Button className="mt-8 bg-white hover:bg-zinc-200 text-black rounded-none h-12 px-6 font-medium tracking-tight">
              Book a discovery call <ArrowUpRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>

        <div className="md:col-span-2">
          <p className="text-xs uppercase tracking-widest text-white/40 mb-4">Explore</p>
          <ul className="space-y-2">
            {NAV.map((n) => (
              <li key={n.to}>
                <Link to={n.to} className="text-sm text-white/70 hover:text-white">{n.label}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-2">
          <p className="text-xs uppercase tracking-widest text-white/40 mb-4">Stack</p>
          <ul className="space-y-2 text-sm text-white/70">
            <li>Power BI · Tableau</li>
            <li>Qlik · Sisense</li>
            <li>Snowflake · Oracle</li>
            <li>SQL Server · Zoho</li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <p className="text-xs uppercase tracking-widest text-white/40 mb-4">Contact</p>
          <p className="text-sm text-white/70">info@pragmaanalyticsgroup.com</p>
          <p className="text-sm text-white/70 mt-1">+1 (281) 305-8509</p>
          <p className="text-sm text-white/50 mt-4">
            Houston · Austin · Dallas · Atlanta · Remote
          </p>
        </div>
      </div>

      <div className="mt-16 -mx-6 lg:-mx-12">
        <div className="select-none leading-none">
          <p
            data-testid="footer-mega-text"
            className="font-display text-[24vw] leading-[0.85] tracking-tighter text-white/[0.06] text-center"
          >
            PRAGMA.
          </p>
        </div>
      </div>

      <div className="pt-8 mt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <p className="text-xs text-white/50">
          © {new Date().getFullYear()} Pragma Analytics Group. All rights reserved.
        </p>
        <p className="text-xs text-white/50">
          Data & BI consulting · Engineered for outcomes
        </p>
      </div>
    </div>
  </footer>
);

const Layout = ({ children, theme = "dark" }) => {
  useEffect(() => {
    document.documentElement.classList.add("dark");
  }, []);
  return (
    <div className={theme === "dark" ? "bg-[#0a0a0a] text-white" : "bg-white text-[#09090b]"}>
      <Nav />
      <main className="pt-16">{children}</main>
      <Footer />
    </div>
  );
};

export default Layout;
